import { useEffect, useState, useRef, useContext, useMemo } from "react";
import { getQuickSheets } from "../API/quickSheets";
import QuickSheetsListView from "../components/QuickSheetsListView";
import QuickSheetsGridView from "../components/QuickSheetsGridView";
import { ProductsModal, ProductsModalButton } from "../components/ProductsModal";
import ProvidersModal from "../components/ProvidersModal";
import TerritoriesModal from "../components/TerritoriesModal";
import Tooltip from "../components/Tooltip";
import { AppContext } from "../util/AppContext";
import PreviewModal from "../components/PreviewModal";
import { downloadTemplate, trackDocumentRequest, trackingEventType } from "../API/documentGenerate";
import ToastAlert from "../components/ToastAlert";
import { getConfigValue, isProfessionalPrintingEnabledForProduct } from "../util/getConfigValue";
import { useErrorBoundary } from "react-error-boundary";
import { useBreadcrumbs } from "../util/BreadcrumbContext";
import PageTitle from "../components/PageTitle";
import PageContextMenu from "../components/PageContextMenu";
import { useNavigate, Link } from "react-router-dom";

function QuickSheetsPage() {
    const [appState, setAppState] = useContext(AppContext);
    const [isListView, setIsListView] = useState(true);
    const [isSortAscending, setIsSortAscending] = useState(true);
    const [documents, setDocuments] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingDocument, setIsLoadingDoucment] = useState(false);
    const isLoadingDocumentRef = useRef(false);
    const [hasNoResults, setHasNoResults] = useState(false);

    const [showProductsModal, setShowProductsModal] = useState(false);
    const [showProvidersModal, setShowProvidersModal] = useState(false);
    const [showTerritoriesModal, setShowTerritoriesModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    const [markets] = useState(JSON.parse(sessionStorage.getItem('marketsForUser')) || []);
    const [territories, setTerritories] = useState([]);

    const [productImageSource, setProductImageSource] = useState();
    const [showToast, setShowToast] = useState(false);
    const [toastTitle, setToastTitle] = useState('');
    const [toastMessage, setToastMessage] = useState('');

    const [currentProduct, setCurrentProduct] = useState(null);
    const [currentProvider, setCurrentProvider] = useState(null);
    const [currentTerritory, setCurrentTerritory] = useState(null);

    const [showProviderTooltip, setShowProviderTooltip] = useState(false);
    const providerTooltipTarget = useRef(null);

    const [showTerritoryTooltip, setShowTerritoryTooltip] = useState(false);
    const territoryTooltipTarget = useRef(null);

    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfFileName, setPdfFileName] = useState('');
    const [quickSheetsId, setQuickSheetsId] = useState('');
    const [previewQuickSheetProductId, setPreviewQuickSheetProductId] = useState(null);

    const { showBoundary } = useErrorBoundary();

    const [canResetProvider, setCanResetProvider] = useState(false);
    const [canResetTerritory, setCanResetTerritory] = useState(false);
    const {setBreadcrumbs } = useBreadcrumbs();
    const [canChangeTerritory, setCanChangeTerritory] = useState(true);
    const useStickyNavigation = getConfigValue(appState.config.CreateSalesAid?.Features?.StickyNavigation?.Enabled, appState)?.Value === 'true';

    const sortedDocuments = useMemo(() => {
        return [...documents].sort((a, b) => {
            const firstName = a?.documentName || '';
            const secondName = b?.documentName || '';
            const comparison = firstName.localeCompare(secondName, undefined, { sensitivity: 'base' });

            return isSortAscending ? comparison : -comparison;
        });
    }, [documents, isSortAscending]);

    const navigate = useNavigate();

    useEffect(() => {
        setBreadcrumbs([
            { label: getConfigValue(appState?.config?.QuickSheets?.Download?.Title, appState)?.Value, path: "/QuickSheetsPage"}
        ]);
    }, [setBreadcrumbs]);

    useEffect(() => {
        if (appState?.currentProduct === "") {
            setCurrentProduct(appState?.productDetails[0]);
            setProductImageSource(appState?.productDetails[0]?.imagePath);
            setAppState({ ...appState, currentProduct: appState?.productDetails[0] });
        } else {
            setCurrentProduct(appState?.currentProduct);
            setProductImageSource(appState?.currentProduct?.imagePath);

            if (appState?.isQuickSheetMessageType) {
                setCurrentTerritory(appState?.territory);
                setCurrentProvider(appState?.prescriber);
            }
        }

        const distinctTerritories = getDistinctTerritories();
        if (distinctTerritories.length === 1) {
            setCurrentTerritory(distinctTerritories[0]);
            setCanResetTerritory(true);
        }

    }, [appState, setAppState])

    useEffect(() => {
        if (currentProduct) {
            loadQuickSheets(currentProduct.productId || currentProduct.clientProductId, true, false, currentTerritory?.id || null, currentProvider?.id || null);
        }
    }, [currentProduct, currentTerritory, currentProvider]);

    useEffect(() => {
        if(!currentProduct?.clientProductId || !markets){
            return;
        }
        const userTerritories = getUserTerritories(markets, currentProduct.clientProductId);
        setTerritories(userTerritories);
        if(currentTerritory && !userTerritories.find(t => t.id === currentTerritory.id)){  
            setCurrentTerritory(null);
        }
    }, [markets, currentProduct]);

    function getUserTerritories(markets, clientProductId) {
        const territories = [];

        if (!markets || markets.length === 0) {
            return territories;
        }

        markets.forEach((market) => {
            market.territories.filter((territory) => territory.productId.includes(clientProductId)).forEach((territory) => {
                territories.push({ ...territory, marketName: market.name });
            })
        });

        return territories;
    }

    function loadQuickSheets(productId, includeActive = true, includeExpired = true, territoryId = null, targetId = null) {
        setIsLoading(true);
        getQuickSheets(productId, includeActive, includeExpired, territoryId, targetId)
            .then((data) => {
                if (data.length === 0 && documents.length === 0) {
                    setHasNoResults(true);
                    setDocuments([]);
                } else {
                    setHasNoResults(false);
                    const filteredDocuments = data.filter((e) => e.productId === currentProduct.clientProductId);
                    setDocuments(filteredDocuments);
                    const nationalQuicksheetsCount = filteredDocuments.filter(item => item.territories?.length === 0);
                    setCanChangeTerritory(nationalQuicksheetsCount.length > 0 || getDistinctTerritories().length > 1);
                }
                setIsLoading(false);
            })
            .catch((error) => {
                showBoundary(error);
                setIsLoading(false);
            });
    }

    function getDistinctTerritories() {
        const allTerritories = Array.from(markets
            .flatMap(market => market.territories || [])
            .reduce((map, obj) => {
            if (obj?.id) {
                map.set(obj.id, obj);
            }
            return map;
            }, new Map())
            .values()
        );

        return allTerritories;        
    }

    function onProductClick(product) {
        setCurrentProduct(product);
        setProductImageSource(product.imagePath);
        setShowProductsModal(false);
        setAppState({ ...appState, currentProduct: product });
    }

    function onProviderClick(provider) {
        setCurrentProvider(provider);
        setCanResetProvider(true);
        setShowProvidersModal(false);
    }

    function onTerritoryClick(territory) {
        setCurrentTerritory(territory);
        setCanResetTerritory(true);
        setShowTerritoriesModal(false);
    }

    function onResetProvider() {
        setCanResetProvider(false);
        setCurrentProvider(null);
    }

    function onResetTerritory() {
        setCanResetTerritory(false);
        setCurrentTerritory(null);
    }

    function displayTerritory(territory) {
        if (!territory) {
            return '-';
        }

        if (territory?.name) {
            return territory.name;
        }
    }

    function displayProvider(provider) {
        if (!provider) {
            return '-';
        }
        return currentProvider && currentProvider.displayName;
    }

    function triggerToastAlert(title, message) {
        setToastTitle(title);
        setToastMessage(message);
        setShowToast(true);
    }

    async function downloadDocument(id) {
        if (isLoadingDocumentRef.current) {
            return;
        }
        try {
            isLoadingDocumentRef.current = true;
            setIsLoadingDoucment(true);
            const response = await downloadTemplate(id);
            return response;
        } catch (ex) {
            console.error(ex);
            triggerToastAlert("Error", getConfigValue(appState?.config?.QuickSheets?.Download?.DownloadError, appState)?.Value || null);
        } finally {
            isLoadingDocumentRef.current = false;
            setIsLoadingDoucment(false);
        }
    }

    async function onPrintDocument(quickSheet) {
        const response = await downloadDocument(quickSheet.documentId);
        const pdfUrl = URL.createObjectURL(response.blob);
        window.open(pdfUrl, "PRINT");
        trackDocumentRequest(trackingEventType.QuickSheetPrint, null, null, quickSheet.documentId);
    }

    async function onDownloadDocument(quickSheet) {
        const response = await downloadDocument(quickSheet.documentId);
        const link = document.createElement('a');
        link.href = URL.createObjectURL(response.blob);
        link.download = response.fileName;
        link.click();
        trackDocumentRequest(trackingEventType.QuickSheetDownload, null, null, quickSheet.documentId);
    }

    async function onPreviewDoucment(quickSheet) {
        setPdfUrl(null);
        setShowPreviewModal(true);
        const response = await downloadDocument(quickSheet.documentId);
        setPdfUrl(URL.createObjectURL(response.blob));
        setQuickSheetsId(quickSheet.documentId);
        setPreviewQuickSheetProductId(quickSheet.productId);
        setPdfFileName(response.fileName);
    }

    async function onSendToProfessionalPrinting(quicksheet) {
        const response = await downloadDocument(quicksheet.documentId);
        const url = URL.createObjectURL(response.blob);
		navigate('/createProfessionalPrintingDocument', {
			state: { pdfURL: url, fileName: response.fileName, isQuickSheet: true, quickSheetId: quicksheet.documentId, returnURL: '/QuickSheetsPage' },
		});
    }

    return (
        <>
            <div className="container-xl">
                <div className="row">
                    <div className="d-flex justify-content-between align-items-center mb-3 mt-3 mt-4">
                        <PageTitle title={getConfigValue(appState?.config?.QuickSheets?.Download?.Title, appState)?.Value || null} />
                        <PageContextMenu includeExportOption={false} />
                    </div>
                </div>
            </div>
            <div className="container-xl mt-3 mt-md-4">
                <div className="row d-flex align-items-md-start mb-5">
                    <div className="col-12 col-md-4 mb-3 mb-md-0">
                        <ProductsModalButton
                            setShowProductsModal={setShowProductsModal}
                            productImageSource={productImageSource}
                            productName={currentProduct?.name}
                        ></ProductsModalButton>
                    </div>
                    <div className="col-12 col-md-4 mb-3 mb-md-0">
                        <div className="text-start d-block w-100">
                            <button onClick={() => setShowProvidersModal(true)} className="border-0 px-0 bg-transparent text-start d-block w-100" data-bs-toggle="modal" data-bs-target="#selectTarget">
                                <div className="border border-dark-subtle card p-2 px-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <p className="my-0 color-grey-d1 type-14">Provider</p>
                                            <p className="my-0 type-14">{displayProvider(currentProvider)}</p>
                                        </div>
                                    </div>
                                </div>
                            </button>
                            <div className="d-flex justify-content-between align-items-center mt-2">
                                <div className="d-flex align-items-center">
                                    <button onClick={() => setShowProvidersModal(true)} data-bs-toggle="modal" data-bs-target="#selectTarget" aria-hidden="true" tabIndex="-1" className="btn-reset m-0 type-12 color-primary text-decoration-underline text-decoration-hover-none">Select target</button>
                                    {canResetProvider && <button type="button" onClick={onResetProvider} className="btn btn-link btn-reset type-12 ms-4">Reset</button>}
                                </div>
                                <button type="button" className="btn-reset"
                                    id="pd-btn-tooltip-printrestriction-row4">
                                    <span ref={providerTooltipTarget} onMouseOver={() => setShowProviderTooltip(true)} onMouseOut={() => setShowProviderTooltip(false)} className="far fa-circle-question type-16 color-primary"></span>
                                    <Tooltip
                                        text="Some sheets are visible only by specifying a target."
                                        ref={providerTooltipTarget}
                                        showTooltip={showProviderTooltip}
                                        index={1}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-md-4 mb-3 mb-md-0">
                        <div className="text-start d-block w-100">
                            <button disabled={!canChangeTerritory} onClick={() => setShowTerritoriesModal(true)} className={`border-0 px-0 bg-transparent text-start d-block w-100 ${canChangeTerritory ? "" : "disabled"}`} data-bs-toggle="modal" data-bs-target="#selectTerritories">
                                <div className="border border-dark-subtle card p-2 px-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <p className="my-0 color-grey-d1 type-14">Territory</p>
                                            <p className="my-0 type-14">{displayTerritory(currentTerritory)}</p>
                                        </div>
                                    </div>
                                </div>
                            </button>
                            <div className="d-flex justify-content-between align-items-center mt-2">
                                <div className="d-flex align-items-center">
                                    <button disabled={!canChangeTerritory} onClick={() => setShowTerritoriesModal(true)} type="button" data-bs-toggle="modal" data-bs-target="#selectTarget" aria-hidden="true" tabIndex="-1" className={`btn-reset m-0 type-12 color-primary text-decoration-underline text-decoration-hover-none ${canChangeTerritory ? "" : "disabled"}`}>Select territory</button>
                                    {canResetTerritory && canChangeTerritory && <button type="button" onClick={onResetTerritory} className="btn btn-link btn-reset type-12 ms-4">Reset</button>}
                                </div>
                                <button disabled={!canChangeTerritory} type="button" className={`btn-reset ${canChangeTerritory ? "" : "disabled"}`}
                                    id="pd-btn-tooltip-printrestriction-row4">
                                    <span ref={territoryTooltipTarget} onMouseOver={() => setShowTerritoryTooltip(true)} onMouseOut={() => setShowTerritoryTooltip(false)} className="far fa-circle-question type-16 color-primary"></span>
                                    <Tooltip
                                        text="Some sheets are visible only by specifying a territory."
                                        ref={territoryTooltipTarget}
                                        showTooltip={showTerritoryTooltip}
                                        index={1}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row d-flex align-items-center my-5 mb-lg-3 ">
                    <div className="col-12 col-md-10">
                        <h1 className="type-24 type-bold text-primary ag_message_title py-3">{getConfigValue(appState?.config?.QuickSheets?.Download?.Group?.Title, appState)?.Value || null}</h1>
                    </div>
                    <div className="col-12 col-md-2 mt-2 mt-lg-0 d-flex justify-content-md-end">
                        <div className="d-flex">
                            <button
                                onClick={() => setIsSortAscending((previousValue) => !previousValue)}
                                className="btn btn-outline-primary btn-icon-only d-lg-flex me-2"
                                aria-label={`Sort by document name ${isSortAscending ? 'A to Z' : 'Z to A'}`}>
                                <span className={`fa-solid ${isSortAscending ? 'fa-arrow-up-short-wide' : 'fa-arrow-down-wide-short'} btn-icon`}></span>
                            </button>
                            <button onClick={() => setIsListView(true)} className={`btn btn-outline-primary btn-icon-only d-lg-flex me-2 ${isListView ? 'active' : ''}`}><span className="fa-regular fa-list btn-icon"></span></button>
                            <button onClick={() => setIsListView(false)} className={`btn btn-outline-primary btn-icon-only d-lg-flex ${!isListView ? 'active' : ''}`}><span className="fa-regular fa-grid-2 btn-icon"></span></button>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">

                        {isListView ? (
                            <QuickSheetsListView
                                documents={sortedDocuments}
                                onPrintDocument={onPrintDocument}
                                onDownloadDocument={onDownloadDocument}
                                isLoading={isLoading}
                                hasNoResults={hasNoResults}
                                onPreviewDoucment={onPreviewDoucment}
                                onSendToProfessionalPrinting={onSendToProfessionalPrinting}
                            />
                        ) : (
                            <QuickSheetsGridView
                                documents={sortedDocuments}
                                onPrintDocument={onPrintDocument}
                                onDownloadDocument={onDownloadDocument}
                                isLoading={isLoading}
                                hasNoResults={hasNoResults}
                                onPreviewDoucment={onPreviewDoucment}
                                onSendToProfessionalPrinting={onSendToProfessionalPrinting}
                            />
                        )}

                    </div>
                </div>
                <div className="my-5"></div>
            </div>

            {appState?.isQuickSheetMessageType && 
                <div className={`${useStickyNavigation ? 'button-bottom-container-sticky-low-z' : ''}`}>
                    <div className='container-xl'>
                        <div className='row justify-content-center justify-content-lg-between py-3'>
                            <div className='col-10 col-sm-8 col-md-6 col-xl-5'>
                                <div className='d-flex flex-column flex-lg-row'>
                                    <div className='flex-fill w-100 ms-lg-3 pe-3'></div>
                                </div>
                            </div>
                            <div className='w-100 d-lg-none'></div>
                            <div className='col-12'>
                                <div className="d-flex flex-column flex-md-row-reverse" style={{ float: "right" }}>
                                    <Link to='/MessageSelectionPage' className='btn btn-secondary btn-lg pe-7 flex-fill w-100'>
                                        {getConfigValue(appState.config?.Common?.Back, appState)?.Value}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            }


            <ProductsModal
                show={showProductsModal}
                setShow={setShowProductsModal}
                onProductClick={onProductClick}
            />

            <ProvidersModal
                show={showProvidersModal}
                setShow={setShowProvidersModal}
                onProviderClick={onProviderClick}
                currentProvider={currentProvider}
            />

            <TerritoriesModal
                show={showTerritoriesModal}
                setShow={setShowTerritoriesModal}
                markets={markets}
                territories={territories}
                currentTerritory={currentTerritory}
                onTerritoryClick={onTerritoryClick}
            />

            <PreviewModal
                show={showPreviewModal}
                setShow={setShowPreviewModal}
                onGenerateClick={null}
                isLoading={isLoading}
                pdfUrl={pdfUrl}
                showGenerateBack={false}
                showPrintDownload={true}
                pdfFileName={pdfFileName}
                supportsProfessionalPrinting={isProfessionalPrintingEnabledForProduct(appState, previewQuickSheetProductId)}
                skipPreviewTrack={false}
                showProviderTerritory={false}
                isQuickSheet={true}
                quickSheetId={quickSheetsId}
                returnURL={'/QuickSheetsPage'}
            />
            <ToastAlert show={showToast} setShow={setShowToast} title={toastTitle} message={toastMessage} />
        </>
    );
}

export default QuickSheetsPage;